/**
 * アカウント詳細の「相談予約」「テキスト受付」ブロック用 — 取得と並べ替えの単一ソース。
 *
 * 事務局（Operator/tabs/UserList）と管理サイト（Teacher/tabs/Students）の 2 画面が同じ利用者 1 人分の
 * 進行中の相談を同じ順序で出すための共有 mixin。並べ替えは純関数（this 非依存・単体テスト対象）、
 * 取得は this.axios を使う 2 メソッドだけに閉じる。
 *
 * 設計上の前提:
 * - 取得は「詳細を開いたとき」に親の changeOpenIds から呼ぶ。管理サイトの詳細スロットは常時描画
 *   （v-slot:main に v-if が無い）なので、子部品の created() に取得を置くと開く前に全行分が走る。
 *   閉じてもキャッシュは消さない（開くたびに取り直す＝「開いた時点で最新」を事務局の現行と同じ粒度で満たす）。
 * - 予約の並びは 実施日時（schedule.date）→ お申込日時（created_at）→ id の昇順。指示は
 *   「実施日時、受付日時の早い日時が上」で、同時刻 2 件の順序を取得順任せにしないため第 2 キーを持つ。
 *   2 画面で同じ関数を使うのは、同じ 2 件が画面ごとに別順になるのを防ぐため。
 * - schedule.date は ISO-UTC・created_at は naive JST 文字列。比較は moment の instant 差分で行い、
 *   壁時計文字列の比較へ退化させない（両辺が同じ表現どうしなので端末 TZ に依存しない）。
 * - /v1/messagegroup/get/list に sort:true を渡さない（渡すと data が {answered, progress, operator} の
 *   オブジェクトに化けて配列で受けられない）。/v2/reserve/get/list に afterNow を渡さない
 *   （eager-load の制約で行を絞らず、過去分の schedule が null になって予約部品が落ちる）。
 * - worryTypesOf は 3 形を受ける。/v1/messagegroup/get/list は自身の type だけ配列化して返すが、
 *   customer.messageGroups[].type は counseling_types の行のまま（type.type が JSON 文字列）で届く。
 */
import moment from 'moment';

/** instant 差分。片方が欠損なら欠損側を後ろへ送る（落とさない）。 */
function diffInstant(a, b) {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return moment(a).diff(moment(b));
}

function compareId(a, b) {
  return Number(a.id || 0) - Number(b.id || 0);
}

/**
 * 予約を 実施日時 → お申込日時 → id の昇順に並べた新配列を返す（入力は変更しない）。
 * @param {Array<{id:number, created_at:string, schedule:{date:string}}>} rows
 * @returns {Array} 新配列
 */
function sortReservesBySchedule(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => diffInstant(a.schedule && a.schedule.date, b.schedule && b.schedule.date)
    || diffInstant(a.created_at, b.created_at)
    || compareId(a, b));
}

/**
 * テキスト相談を 受付日時（created_at）→ id の昇順に並べた新配列を返す（入力は変更しない）。
 * @param {Array<{id:number, created_at:string}>} rows
 * @returns {Array} 新配列
 */
function sortTextIntakes(rows) {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => diffInstant(a.created_at, b.created_at) || compareId(a, b));
}

/**
 * お悩み大項目の配列を取り出す。配列 / JSON 文字列 / {type} の 3 形を受け、数値配列に正す。
 * 壊れた文字列・未知の形は空配列（呼び出し側は空なら行ごと出さない）。
 * @param {Array|string|{type:any}|null|undefined} v
 * @returns {Array<number>}
 */
function worryTypesOf(v) {
  if (Array.isArray(v)) return v.map(Number).filter((n) => !Number.isNaN(n));
  if (typeof v === 'string') {
    try {
      return worryTypesOf(JSON.parse(v));
    } catch (e) {
      return [];
    }
  }
  if (v && typeof v === 'object' && 'type' in v) return worryTypesOf(v.type);
  return [];
}

export default {
  methods: {
    sortReservesBySchedule,
    sortTextIntakes,
    worryTypesOf,

    /**
     * 利用者 1 人分の予約中（flag=1）のオンライン・バーチャル健康相談を取得して並べ替える。
     * counseling_type ごとに 1 本ずつ（計 2 本）叩き、行に counseling_type を付与してマージする
     * （応答自体は種別を持たないため。ReserveInfoBox がアイコンの出し分けに使う）。
     * 失敗はログに残して空配列（画面はブロックを出さない＝事務局の現行と同じ fail-quiet）。
     * @param {number} userId 利用者 id（ハイライト HTML を剥がした数値）
     * @returns {Promise<Array>} 並べ替え済みの予約
     */
    async fetchFutureReserves(userId) {
      const params = {
        from_user_id: userId,
        orderBy: 'asc',
        progress: 1,
        withType: true,
      };
      const fetchType = async (counselingType) => {
        const response = await this.axios({
          method: 'GET',
          url: '/v2/reserve/get/list',
          params: { ...params, counseling_type: counselingType },
        });
        const rows = (response.data.result && response.data.result.data) || [];
        rows.forEach((reserve) => {
          reserve.counseling_type = counselingType;
        });
        return rows;
      };
      try {
        // オンライン健康相談=1 → バーチャル健康相談=3 の順に逐次で叩く（事務局の現行と同じ形）
        const onlines = await fetchType(1);
        const virtuals = await fetchType(3);
        return sortReservesBySchedule([...onlines, ...virtuals]);
      } catch (error) {
        if (error.response) console.log(error.response.data);
        else console.log(error);
        return [];
      }
    },

    /**
     * 利用者 1 人分の受付中（flag=1）・下書き（flag=21）のテキスト健康相談を取得して並べ替える。
     * showProgress=1 が flag IN (1,21) の固定セットで、受付中だけを取る口は無い（下書きも利用者からは未回答）。
     * @param {number} userId 利用者 id
     * @returns {Promise<Array>} 受付日時昇順のテキスト相談
     */
    async fetchTextIntakes(userId) {
      try {
        const response = await this.axios({
          method: 'GET',
          url: '/v1/messagegroup/get/list',
          params: {
            user_id: userId,
            target_type: 1,
            showProgress: 1,
            orderBy: 'asc',
          },
        });
        const rows = (response.data.groups && response.data.groups.data) || [];
        return sortTextIntakes(rows);
      } catch (error) {
        if (error.response) console.log(error.response.data);
        else console.log(error);
        return [];
      }
    },
  },
};
